import classnames from 'classnames'
import { useCallback, useEffect, useMemo } from 'react'

import { ProductCard as ProductCardC } from '@src/components/product-card/product-card.component'
import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import BATCHED_EVENT_LIMIT from '@src/constants/batchedEventLimit'
import { STATSIG_EXPERIMENTS, useStatsigExperimentBucket } from '@src/experiments/statsig-integration'
import { LoaderWrapper } from '@src/quince-components/src'
import Swiper from '@src/quince-components/src/atoms/swiper'
import { DOTS_POSITION } from '@src/quince-components/src/atoms/swiper/Swiper'
import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import Product from '@src/quince-site-common/src/domains/Product'
import { PRODUCT_TYPES } from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper, { isProductBeauty, isProductVitamin } from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useFetch from '@src/quince-site-common/src/hooks/useFetch'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import productRecommendationService from '@src/quince-site-common/src/services/productRecommendationService'
import { batchedAnalytics } from '@src/quince-site-common/src/utils/batched-analytics'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { getProductDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductDataForBatchedAnalytics'
import { getProductTagsDataForBatchedAnalytics } from '@src/quince-site-common/src/utils/getProductTagsDataForBatchedAnalytics'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { RECOMMENDED_SECTION_IDS } from '@src/quince-site-pdp/src/contexts/productPageContext.constants'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { useIsNewRecommendationsDesign } from '@src/quince-site-pdp/src/templates/productPage/productPage.utils'
import { FINDING_METHOD_AGGREGATE } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { productContextInitialState } from '../../../contexts/ProductPageContext'
import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'
import useProductPageContext from '../../../hooks/useProductContext'
import { getSelectedColor } from '../utils'

import { TRACKING_METADATA, getTrackingMetaData } from './pairWithProducts.helpers'
import styles from './pairWithProducts.module.scss'

const MOBILE_SLIDES = 4
const DESKTOP_SLIDES = 2
const COMPLETE_ROUTINE_TITLE = 'Complete your routine'

const PairWithProducts = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const { zipCode } = useZipCode()
  const {
    recommendationUIData: { [RECOMMENDED_SECTION_IDS.SLOT1]: filteredPairWithProducts },
    updateRecommendationAPIData,
    isRecommendationLoading,
  } = useProductPageContext()
  const product = 'bundle' in props ? props.bundle : props.product
  const selectedOptions = useAppSelector<ProductVariantOption[]>(pdpStateManager, pdpStateManager.getSelectedOptions)
  const productId = useAppSelector<string | undefined>(pdpStateManager, pdpStateManager.getProductIdFromRedux)
  const selectedColorValue = selectedOptions.filter(o => o.name == 'Color')[0]?.value

  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const shouldShowNewCTLSection = useIsNewRecommendationsDesign()
  const isNewCTLMobile = shouldShowNewCTLSection && !isDesktop

  const {
    fetchData: fetchPairWithProducts,
    isLoading: isPairWithLoading,
    response: pairWithProducts,
  } = useFetch<IProductCard[]>(productRecommendationService.getPairWithProducts, {
    onlyReturnLatestFetch: true,
  })

  const productLimit = 6

  //Should be invoked incase of Quince A/B bucket
  useEffect(() => {
    let shopifyProductId
    if (product.type === PRODUCT_TYPES.BUNDLE) {
      shopifyProductId =
        product.customBundleProducts?.length &&
        productHelper.getShopifyProductId(product.customBundleProducts[0] as unknown as Product, selectedOptions)
    } else {
      shopifyProductId = productHelper.getShopifyProductId(product, selectedOptions)
    }

    if (!selectedColorValue || productId !== product.id.toString()) return
    fetchPairWithProducts(shopifyProductId, selectedOptions, product.gender, true, zipCode, productLimit)
      .then(res => {
        if (res) {
          updateRecommendationAPIData(RECOMMENDED_SECTION_IDS.SLOT1, {
            ...productContextInitialState.recommendationUIData[RECOMMENDED_SECTION_IDS.SLOT1],
            data: res,
          })
        }
      })
      .catch((e: unknown) => {
        captureException(e, {
          module: MODULES.RECOMMENDATION,
          feature: FEATURES.PAIRED_PRODUCTS,
        })
      })
  }, [selectedColorValue, productId])

  const isLoadedWithData = !isRecommendationLoading && !isPairWithLoading && filteredPairWithProducts.data.length
  const isLoadedWithNoData =
    !isPairWithLoading &&
    !isRecommendationLoading &&
    pairWithProducts?.length === 0 &&
    filteredPairWithProducts.data.length === 0

  const selectedColor = getSelectedColor(selectedOptions)

  const displayTitle = useMemo(() => {
    if (isProductVitamin(product as Product) || isProductBeauty(product as Product)) {
      return COMPLETE_ROUTINE_TITLE
    }
    return filteredPairWithProducts.module_title
  }, [product, filteredPairWithProducts])

  const getIsVisibleTrackParams = useCallback(() => {
    const productId = product.id
    return {
      action: 'complete_the_look',
      value: true,
      context: {
        productId,
        selectedColor,
        ...getTrackingMetaData(displayTitle).context,
        findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
      },
    }
  }, [product.id, selectedColor])

  const getRecommendationVisibleTrackParams = useCallback(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW, filteredPairWithProducts)
  }, [product.id, filteredPairWithProducts])

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(
    getIsVisibleTrackParams,
    getRecommendationVisibleTrackParams,
  )

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem:
      (shouldShowNewCTLSection ? styles.productCardWidthMobileNewDesign : styles.productCardWidthMobile) ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const carouselArrowClickTracker = ({ direction }: { direction: string }) => {
    const clickDirectionEvent =
      direction === 'prev' ? TELEMETRY_EVENT_NAMES.CAROUSEL_LEFT_CLICK : TELEMETRY_EVENT_NAMES.CAROUSEL_RIGHT_CLICK
    trackEvent(clickDirectionEvent, {
      module_name: filteredPairWithProducts.module_name,
      module_id: filteredPairWithProducts.module_id,
    })
  }

  const filteredPairWithProductsFinal = filteredPairWithProducts.data.length && filteredPairWithProducts.data

  const productViewTracker = `${product.slug}_${TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW}_complete_the_look`
  useEffect(() => {
    if (productViewTracker) {
      batchedAnalytics.initializeBatchedAnalyticsEvent({
        tracker: productViewTracker,
        eventLimit: BATCHED_EVENT_LIMIT[TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW],
        debouncePeriod: 1000,
      })
      batchedAnalytics.trackBatchedAnalytics({
        id: `internalAnalytics-${productViewTracker}`,
        tracker: productViewTracker,
        callback: ({ events }) => {
          const productList = events.map(event => getProductDataForBatchedAnalytics(event))
          const tagsCount = getProductTagsDataForBatchedAnalytics(productList)
          trackEvent(TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW, {
            productList: productList,
            tagsCount: tagsCount,
            ...getTrackingMetaData(displayTitle).context,
          })
        },
      })
    }
  }, [])

  const experimentBucket = useStatsigExperimentBucket(STATSIG_EXPERIMENTS.CTL_A_VS_A_EXPERIMENT)

  return (
    <div
      ref={isLoadedWithData ? trackIsVisibleRef : undefined}
      className={classnames(
        styles.pairWithProducts,
        isLoadedWithNoData && styles.pairWithProducts__hidePairsWith,
        experimentBucket,
      )}
    >
      {isRecommendationLoading || isPairWithLoading || isLoadedWithData ? (
        <h2 className={styles.pairWithProducts__title}>{displayTitle}</h2>
      ) : null}
      <LoaderWrapper
        showLoader={isRecommendationLoading || isPairWithLoading}
        minHeight={!isDesktop && (isRecommendationLoading || isPairWithLoading) ? '13.5rem' : '31rem'}
      >
        <>
          {(!isPairWithLoading || !isRecommendationLoading) && (
            <Swiper
              showDots={isNewCTLMobile}
              showNavigator={isDesktop}
              numberOfVisibleSlides={isNewCTLMobile ? MOBILE_SLIDES : DESKTOP_SLIDES}
              variableWidth
              dotsPosition={DOTS_POSITION.BOTTOM}
              tracker={'pair_with_section'}
              id={'pair_with_section'}
              arrowOnClick={carouselArrowClickTracker}
              trackingMetaData={getTrackingMetaData(displayTitle)}
            >
              {filteredPairWithProductsFinal
                ? filteredPairWithProductsFinal.map((productCard: IProductCard, idx: number) => {
                    const initialSelectedVariant = getInitialSelectedVariant({
                      cardVariants: productCard.product.cardVariants,
                    })
                    return (
                      <ProductCardC
                        key={`${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`}
                        className={classnames(
                          shouldShowNewCTLSection ? styles.productCard__newDesign : styles.productCard,
                          idx === filteredPairWithProductsFinal.length - 1 && styles.productCard__lastEle,
                        )}
                        titleClassName={styles.productCard__productCardTitle}
                        priceClassName={styles.productCard__productCardPrice}
                        productPrefixPriceClassName={styles.productCard__productPrefixPrice}
                        addToCartClassName={styles.productCard__addToCart}
                        wishlistIconClassName={styles.productCard__wishlistIcon}
                        maxWidth={maxWidth}
                        productCard={productCard}
                        hideOptions={true}
                        hideReviews={true}
                        tracker={`pair_with_section__${idx}`}
                        skipTagRefresh={true}
                        productClickTrackConfig={{
                          category: TRACKING_METADATA.category,
                          action: TRACKING_METADATA.action,
                          context: {
                            source_product_id: product.shopifyProductId,
                            source_product_color: getSelectedColorFromSelectedOptions(selectedOptions),
                            isRecommendationProduct: true,
                            productPosition: idx,
                            ...getTrackingMetaData(displayTitle).context,
                            findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
                          },
                        }}
                        nameAndPriceSectionClass={isNewCTLMobile ? styles.nameAndPriceSection : undefined}
                        productPriceClass={isNewCTLMobile ? styles.productPriceClassNewDesign : undefined}
                        productCardLinkClass={isNewCTLMobile ? styles.productCardLink : undefined}
                        productCardImageSectionClass={isNewCTLMobile ? styles.productCardImageSectionClass : undefined}
                        hideWishlistIcon={isNewCTLMobile}
                        productViewTracker={productViewTracker}
                      />
                    )
                  })
                : null}
            </Swiper>
          )}
        </>
      </LoaderWrapper>
    </div>
  )
}

export default PairWithProducts
